import { ImageList, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { SearchBar } from 'src/components/input';
import { NoResultAlert } from 'src/components/noResultAlert';
import {
  filterAssetsBySearchName,
  findMatchedAsset,
} from 'src/components/widget/resourceSelectorWidget/ResourceSelector.helper';
import { useResourceSelector } from 'src/components/widget/resourceSelectorWidget/ResourceSelector.hook';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { ResourceSelectorSidebarHeader } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/resourceSelectorSidebarHeader';
import { ResourceSelectorSidebarItem } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/resourceSelectorSidebarItem';
import { useGetAllAssets } from 'src/hooks';
import { sortAssetsByUploadDate } from 'src/hooks/api/assets/helper';
import { IAsset } from 'src/interfaces';
import { lang } from 'src/utils';

import { SimpleDialog } from '../simpleDialog';
import { AssetUploadModalProps } from './AssetUploadModal.interface';
import {
  ResourceSelectorContent,
  StyledContainer,
} from './AssetUploadModal.styled';

export const AssetUploadModal = (props: AssetUploadModalProps) => {
  const {
    data: assets = [],
    isLoading: isLoadingAssets,
    refetch: refetchAssets,
  } = useGetAllAssets();

  const supportedAssets = assets.filter((asset: IAsset) => {
    const extension = asset.fileName.split('.').pop()?.toLowerCase();

    return props.allowedFileExtension?.includes(extension as string);
  });

  const sortedAssets = sortAssetsByUploadDate(supportedAssets);

  const {
    searchVal,
    selectedAssets,
    onChangeSearchVal,
    onSelectedAssets,
    resetSearchVal,
  } = useResourceSelector();

  const filteredAssets = filterAssetsBySearchName(
    sortedAssets,
    searchVal,
    ResourceType.BACKGROUND,
  );

  let isAssetSelected = false;

  const handleClose = () => {
    resetSearchVal();
    if (!isAssetSelected && props.setSelectedIndex) {
      props.setSelectedIndex(null);
    }
    props.assetUploadModal?.onCloseModal();
  };

  const handleAssetClick = debounce(async (asset: IAsset) => {
    if (!props.validateFile || props.validateFile?.(asset)) {
      isAssetSelected = true;
      try {
        const response = await fetch(asset.thumbnailUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        const blob = await response.blob();

        const file = new File([blob], asset.fileName, {
          type: props.supportedFileType,
        });

        if (props.onSelectAsset) {
          props.onSelectAsset(file);
        }
        if (props.setFieldValue) {
          props.setFieldValue('customizedOutageConfig.stamp.url', file.name, {
            shouldDirty: true,
          });
          props.setFieldValue('uploadedAsset', file);
        }

        onSelectedAssets([asset]);
        handleClose();
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    }
  }, 500);

  return (
    <>
      <SimpleDialog
        closeOnBackdropClick={false}
        open={props.assetUploadModal?.open as boolean}
        modalSize="md"
        hideButton
        title={lang.uploadOrSelectImage}
        subTitle={lang.uploadAnImage}
        onClose={handleClose}
        onClickCancel={handleClose}>
        <ResourceSelectorSidebarHeader
          assetUploadModal={props.assetUploadModal}
          allowedExtensions={props.allowedFileExtension}
          supportedFileType={props.supportedFileType}
          setFieldValue={props.setFieldValue}
          onSelectAsset={props.onSelectAsset}
          refetchAssets={refetchAssets}
          validateFile={props.validateFile}
        />
        <StyledContainer>
          <Typography>{lang.uploadFromSwitchboard}</Typography>
          <SearchBar
            value={searchVal}
            isLoading={isLoadingAssets}
            onInputChange={onChangeSearchVal}
          />
        </StyledContainer>
        <ResourceSelectorContent>
          <ImageList cols={filteredAssets.length > 0 ? 5 : 1} rowHeight={160}>
            {filteredAssets.length > 0 ? (
              filteredAssets.map((asset) => (
                <ResourceSelectorSidebarItem
                  key={asset.assetId}
                  type={ResourceType.PRODUCT}
                  fileName={asset.fileName}
                  src={asset.thumbnailUrl}
                  isSelected={!!findMatchedAsset(selectedAssets, asset.assetId)}
                  onClick={() => handleAssetClick(asset)}
                />
              ))
            ) : (
              <NoResultAlert />
            )}
          </ImageList>
        </ResourceSelectorContent>
      </SimpleDialog>
    </>
  );
};
